import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import GdprConsentCheckbox from '../components/GdprConsentCheckbox';
import { isValidFlexiblePhone, PHONE_FORMAT_MESSAGE, PHONE_PLACEHOLDER, PHONE_REQUIRED_MESSAGE } from '../utils/phoneValidation';
import QuoteRequestSuccess, { QuoteSuccessPrimaryButton } from '../components/QuoteRequestSuccess';
import '../App.css';

const TEMPLATES = [
  { id: 'koltoztetes', label: 'Költöztetés', to: '/ajanlat/koltoztetes' },
  { id: 'csomagszallitas', label: 'Csomagszállítás', to: '/ajanlat/csomagszallitas' },
  { id: 'ikea', label: 'IKEA házhozszállítás', to: '/ajanlat/ikea-hazhozszallitas' },
  { id: 'raklap', label: 'Raklapos szállítás', to: '/ajanlat/raklapos-szallitas' },
  { id: 'kerekpar', label: 'Motorkerékpár / kerékpár szállítás', to: '/ajanlat/kerekpar-szallitas' },
  { id: 'butor', label: 'Bútorszállítás', to: '/ajanlat/butor-szallitas' },
];

const initial = {
  template: '',
  name: '',
  phone: '',
  email: '',
  message: '',
  privacyAccepted: false,
};

export default function AjanlatPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [form, setForm] = React.useState(initial);
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState('');

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setError('');
  };

  const validate = () => {
    if (!form.name.trim()) return t('Add meg a neved.');
    if (!form.phone.trim()) return t(PHONE_REQUIRED_MESSAGE);
    if (!isValidFlexiblePhone(form.phone)) return t(PHONE_FORMAT_MESSAGE);
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return t('Érvényes e-mail címet adj meg.');
    if (!form.message.trim()) return t('Írd le röviden, miben segíthetünk.');
    if (!form.privacyAccepted) return t('gdpr_consent_required');
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    // TODO: POST to backend when available
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (submitted) {
    return (
      <main className="qf-page">
        <div className="qf-inner">
          <QuoteRequestSuccess
            title={t('Köszönjük az ajánlatkérést!')}
            subtitle={t(
              'Az adataidat sikeresen rögzítettük. Munkatársunk hamarosan felveszi veled a kapcsolatot a megadott elérhetőségeken.',
            )}
            primary={
              form.template ? (
                <QuoteSuccessPrimaryButton onClick={() => navigate(form.template)}>
                  {t('Tovább a kiválasztott sablonhoz')}
                </QuoteSuccessPrimaryButton>
              ) : null
            }
            homeLabel={t('Főoldal')}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="qf-page">
      <div className="qf-inner">
        <div className="qf-head">
          <nav className="svc-breadcrumb-trail qf-breadcrumb-trail" aria-label="Útvonal">
            <Link className="svc-breadcrumb-seg" to="/">
              {t('Főoldal')}
            </Link>
            <span className="svc-breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <span className="svc-breadcrumb-current" aria-current="page">
              {t('Árajánlatkérés')}
            </span>
          </nav>
          <p className="qf-kicker">{t('Árajánlatkérés — sablon választás vagy általános űrlap')}</p>
          <h1 className="qf-title">{t('Árajánlatkérés')}</h1>
          <p className="qf-sub">{t('Válassz egy sablont, vagy küldj általános ajánlatkérést.')}</p>
        </div>

        <section className="qf-step" aria-labelledby="ajanlat-templates-h">
          <h2 id="ajanlat-templates-h" className="qf-step-title">
            {t('Sablonok')}
          </h2>
          <p className="qf-sub qf-sub--tight">
            {t('Ha a szolgáltatásodhoz tartozik sablon, ezt válaszd: gyorsabb, pontosabb ajánlat.')}
          </p>

          <div className="quote-hub-grid">
            {TEMPLATES.map((t0) => (
              <Link key={t0.id} className="quote-hub-card" to={t0.to}>
                <div className="quote-hub-card-title">{t(t0.label)}</div>
                <div className="quote-hub-card-cta">{t('Sablon megnyitása →')}</div>
              </Link>
            ))}
          </div>

          <div className="qf-grid quote-hub-picker">
            <div className="field qf-field qf-field--full">
              <label htmlFor="template">{t('Vagy válassz innen sablont (opcionális)')}</label>
              <select
                id="template"
                value={form.template}
                onChange={(e) => update('template', e.target.value)}
              >
                <option value="">{t('— nem választok sablont —')}</option>
                {TEMPLATES.map((t0) => (
                  <option key={t0.id} value={t0.to}>
                    {t(t0.label)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="qf-step" aria-labelledby="ajanlat-contact-h">
          <h2 id="ajanlat-contact-h" className="qf-step-title">
            {t('Elérhetőségek')}
          </h2>
          <div className="quote-hub-contact">
            <div className="quote-hub-contact-item">
              <div className="quote-hub-contact-label">{t('Telefon')}</div>
              <a className="quote-hub-contact-link" href="tel:+36706050279">
                +36 70 605 0279
              </a>
            </div>
            <div className="quote-hub-contact-item">
              <div className="quote-hub-contact-label">{t('E-mail')}</div>
              <a className="quote-hub-contact-link" href="mailto:office@moveandparcel.eu">
                office@moveandparcel.eu
              </a>
            </div>
          </div>
        </section>

        {error && (
          <div className="qf-banner qf-banner--error" role="alert">
            {error}
          </div>
        )}

        <form className="qf-form" onSubmit={handleSubmit}>
          <div className="qf-step">
            <h2 className="qf-step-title">{t('Általános ajánlatkérés')}</h2>

            <div className="qf-grid">
              <div className="field qf-field">
                <label htmlFor="name">{t('Név *')}</label>
                <input id="name" value={form.name} onChange={(e) => update('name', e.target.value)} />
              </div>
              <div className="field qf-field">
                <label htmlFor="phone">{t('Telefonszám')} *</label>
                <input
                  id="phone"
                  type="tel"
                  placeholder={PHONE_PLACEHOLDER}
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                />
              </div>
              <div className="field qf-field qf-field--full">
                <label htmlFor="email">{t('E-mail cím *')}</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                />
              </div>
              <div className="field qf-field qf-field--full">
                <label htmlFor="message">{t('Rövid leírás *')}</label>
                <textarea
                  id="message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  placeholder={t('Mit szeretnél szállíttatni, honnan–hova, kb. méret/súly, kívánt időpont…')}
                />
              </div>
            </div>

            <GdprConsentCheckbox
              id="ajanlat-gdpr"
              checked={form.privacyAccepted}
              onChange={(v) => update('privacyAccepted', v)}
            />

            <div className="qf-actions">
              <button type="submit" className="primary-btn qf-btn-submit">
                {t('Ajánlatkérés küldése')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </main>
  );
}

